export const translations = {
  en: {
    nav: {
      banner: "$0 Risk · $0 Upfront · We Win Together",
      howItWorks: "How It Works",
      benefits: "Benefits",
      products: "Products",
      faq: "FAQ",
      cta: "Start Earning",
    },
    hero: {
      eyebrow: "The Monetization Engine for Creators",
      titleA: "You Bring the Audience.",
      titleB: "We Turn It Into",
      titleC: "Cash.",
      sub: "You're sitting on a goldmine of attention. We help you monetize it. We handle everything: strategy, idea, product, platform, and launch. You stay the face, we handle the rest.",
      microNote: "No upfront cost. We only win if you win.",
      ctaPrimary: "Start Earning With Us",
      ctaSecondary: "See How It Works",
      stats: [
        { k: "0$", v: "Upfront from creators" },
        { k: "100%", v: "Done-for-you build" },
        { k: "∞", v: "Revenue ceiling" },
      ],
    },
    problem: {
      eyebrow: "The Real Problem",
      title: "You Have the Audience.",
      titleItalic: "But Where's the Money?",
      sub: "You're sitting on a goldmine. Your audience trusts you, listens to you, and buys what you recommend. But without a system, that gold stays buried.",
      cards: [
        { t: "Attention Without Revenue", d: "You're creating content that gets engagement, but your bank account doesn't reflect your influence." },
        { t: "No Time to Build Products", d: "Between content creation, DMs, and brand deals, there's zero bandwidth to design, build, and launch a digital product." },
        { t: "No System, No Strategy", d: "Random link-in-bio tools and one-off promotions aren't a business. Without a system, you're leaving money on every post." },
        { t: "Fear of Launching Wrong", d: "You know you should monetize, but the fear of building something nobody buys keeps you stuck in content-only mode." },
      ]
    },
    howItWorks: {
      eyebrow: "How It Works",
      title: "From Audience to Revenue",
      titleItalic: "in 5 Steps",
      steps: [
        { t: "Discovery & Audit", d: "We analyze your audience, niche, and existing content to identify the highest-value monetization opportunity hiding in your following." },
        { t: "Offer Architecture", d: "We design a digital product tailored to your audience's desires: a video course or coaching program built from audience demand and validated pain points." },
        { t: "Build & Package", d: "We handle product creation, sales copy, landing pages, and the entire backend. You stay focused on content. We build the machine." },
        { t: "Launch System", d: "We deploy a proven story-based launch sequence through your existing audience, turning attention into demand and demand into purchases." },
        { t: "Scale & Iterate", d: "Post-launch, we collect data, refine the offer, and optimize for higher conversions. Then we rinse and repeat with your next product." },
      ]
    },
    benefits: {
      eyebrow: "Why Partner With Us",
      title: "We Handle the Backend.",
      titleItalic: "You Keep the Spotlight.",
      cards: [
        { t: "Revenue Without the Workload", d: "We build, launch, and manage your product engine. You keep doing what you're great at: creating content." },
        { t: "Done-For-You Product Creation", d: "From video courses to coaching programs, we handle ideation, production, copy, and packaging end to end." },
        { t: "Proven Launch Systems", d: "No guessing. We deploy battle-tested launch sequences designed to convert your existing audience into buyers." },
        { t: "Partnership, Not Client Work", d: "Zero upfront cost, ever. We only make money when you make money. Pure revenue share means our success is your success." },
        { t: "Built for Your Audience", d: "Every product is reverse-engineered from your audience's pain points, desires, and buying behavior. Not generic templates." },
        { t: "Scalable & Repeatable", d: "One product is just the start. We build systems that let you launch again, diversify revenue, and compound growth." },
      ]
    },
    products: {
      eyebrow: "What We Build",
      title: "Revenue Products,",
      titleItalic: "Engineered for Your Audience",
      sub: "Every product is built from your audience's psychology, not from a template library. Here's what we create:",
      cards: [
        { t: "Video Courses", d: "Structured learning experiences built from your knowledge. We handle curriculum design, production support, and sales infrastructure." },
        { t: "Coaching Programs", d: "High-ticket offers that let your most engaged followers access you directly, packaged with systems that sell and deliver." },
        { t: "Templates & Toolkits", d: "Actionable resources your audience can implement immediately. High perceived value, fast to produce, easy to scale." },
      ]
    },
    faq: {
      eyebrow: "FAQ",
      title: "Questions?",
      titleItalic: "We've Got Answers.",
      items: [
        { q: "How is this different from hiring a marketing agency?", a: "Agencies charge retainers and hand you deliverables. We operate as a revenue partner with zero upfront fees. We design, build, launch, and optimize your product end-to-end and only earn when you do." },
        { q: "Do I need a large following to work with you?", a: "Engagement quality matters more than raw follower count. If you have a tight community of buyers, even a smaller audience can produce significant revenue. We'll assess fit during the discovery audit." },
        { q: "What kind of digital products do you build?", a: "Primarily video courses, high-ticket coaching programs, and high-leverage templates or toolkits. Each is reverse-engineered from your audience's specific pain points and willingness to pay." },
        { q: "How much time do I need to commit?", a: "Minimal. We need a few strategy calls and your face on hero assets and launch content. The rest of the building, copywriting, tech, and ops is on us." },
        { q: "How do you make money?", a: "Pure revenue share. No setup fees, no monthly retainers. Our incentives are 100% aligned with your bottom line." },
        { q: "How fast can I expect results?", a: "The expected timeline from audit to first launch is 4–8 weeks, depending on audience size, engagement, and offer fit." },
        { q: "What if I've never sold anything before?", a: "Even better. We handle product strategy, positioning, and the launch sequence. You focus on showing up for your audience the way you already do." },
        { q: "Is this just AI-generated content slapped on a product?", a: "No. We design original curriculum, write custom sales copy, and engineer launches around your voice and audience. AI is a tool we use, not the product we ship." },
      ]
    },
    finalCta: {
      eyebrow: "Ready to Start?",
      title: "Your Audience Is Already Waiting",
      titleItalic: "to Buy From You.",
      sub: "No upfront fees. No retainers. We only earn when you earn. Limited partnerships available. Apply now and let's extract the gold your audience is sitting on.",
      mail: "Interested in partnering with us? Email us at",
      mailSuffix: "— Name, niche, and Instagram handle.",
      micro: "No spam. No obligations. Just a conversation about growth.",
      tagline: "$0 Risk. $0 Upfront. We Win Together.",
      cta: "Apply to Partner",
    },
    footer: {
      blurb: "Turning creator attention into scalable revenue.",
      rights: "All rights reserved.",
    }
  },
  fr: {
    nav: {
      banner: "0 € de risque · 0 € d'avance · Nous gagnons ensemble",
      howItWorks: "Comment ça marche",
      benefits: "Avantages",
      products: "Produits",
      faq: "FAQ",
      cta: "Commencer",
    },
    hero: {
      eyebrow: "Le système de monétisation pour créateurs",
      titleA: "Vous apportez l'audience.",
      titleB: "Nous la transformons en",
      titleC: "revenus.",
      sub: "Vous créez du contenu. Votre audience vous fait confiance. Nous transformons cette attention en revenus grâce à un système complet de monétisation. De la stratégie au lancement, nous concevons, produisons et déployons des produits digitaux adaptés à votre audience.",
      microNote: "Vous restez sous les projecteurs. Nous opérons dans l'ombre. Aucun coût initial : nous ne gagnons que quand vous gagnez.",
      ctaPrimary: "Commencez à gagner avec nous",
      ctaSecondary: "Voir comment ça marche",
      stats: [
        { k: "0 €", v: "Aucun coût initial" },
        { k: "100%", v: "Clé en main" },
        { k: "∞", v: "Plafond de revenus" },
      ],
    },
    problem: {
      eyebrow: "Le vrai problème",
      title: "Vous avez l'audience.",
      titleItalic: "Mais où est l'argent ?",
      sub: "Votre audience est déjà une source de revenus inexploitée. Elle vous fait confiance, vous écoute et achète vos recommandations. Mais sans système, ce potentiel reste dormant.",
      cards: [
        { t: "De l'attention sans revenus", d: "Vous créez du contenu qui génère de l'engagement, mais votre compte en banque ne reflète pas votre influence." },
        { t: "Pas le temps de créer des produits", d: "Entre la création de contenu, les DM et les partenariats, il n'y a aucune marge pour concevoir et lancer un produit digital." },
        { t: "Aucun système, aucune stratégie", d: "Des outils link-in-bio et des promos ponctuelles ne font pas un business. Sans système, vous laissez de l'argent sur la table." },
        { t: "La peur de mal lancer", d: "Vous savez que vous devriez monétiser, mais la peur de créer un produit que personne n'achète vous bloque." },
      ]
    },
    howItWorks: {
      eyebrow: "Comment ça marche",
      title: "De l'audience au revenu",
      titleItalic: "en 5 étapes",
      steps: [
        { t: "Découverte & Audit", d: "Nous analysons votre audience, votre niche et votre contenu existant pour identifier l'opportunité de monétisation à plus forte valeur." },
        { t: "Architecture de l'offre", d: "Nous concevons un produit digital adapté aux désirs de votre audience : une formation vidéo ou un programme de coaching construit à partir de la demande et des problèmes validés de votre audience." },
        { t: "Création & Packaging", d: "Nous prenons en charge la création du produit, le copywriting, les landing pages et toute l'infrastructure technique. Vous restez focalisé sur le contenu." },
        { t: "Système de lancement", d: "Nous déployons une séquence de lancement éprouvée à travers votre audience existante, transformant l'attention en demande et la demande en ventes." },
        { t: "Optimisation & Croissance", d: "Après le lancement, nous collectons les données, optimisons l'offre et améliorons les conversions. Puis nous recommençons avec votre prochain produit." },
      ]
    },
    benefits: {
      eyebrow: "Pourquoi s'associer avec nous",
      title: "Nous gérons l'exécution.",
      titleItalic: "Vous restez sous les projecteurs.",
      cards: [
        { t: "Du revenu sans la charge de travail", d: "Nous construisons, lançons et gérons votre système de produits. Vous continuez à faire ce que vous faites de mieux : créer du contenu." },
        { t: "Création de produit clé en main", d: "Des formations vidéo au coaching, nous gérons l'idéation, la production, le copywriting et le packaging de A à Z." },
        { t: "Systèmes de lancement éprouvés", d: "Pas d'improvisation. Nous déployons des séquences de lancement testées et approuvées pour convertir votre audience en acheteurs." },
        { t: "Partenariat, pas prestation", d: "Zéro coût initial, jamais. Nous ne gagnons que quand vous gagnez. Le partage des revenus aligne directement notre réussite sur la vôtre." },
        { t: "Conçu pour votre audience", d: "Chaque produit est rétro-conçu à partir des problèmes, désirs et comportements d'achat de votre audience. Pas de templates génériques." },
        { t: "Évolutif & Reproductible", d: "Un produit n'est que le début. Nous construisons des systèmes qui vous permettent de relancer, diversifier et accumuler." },
      ]
    },
    products: {
      eyebrow: "Ce que nous construisons",
      title: "Des produits générateurs de revenus,",
      titleItalic: "conçus pour votre audience",
      sub: "Chaque produit est construit à partir de la psychologie de votre audience, pas d'une bibliothèque de templates. Voici ce que nous créons :",
      cards: [
        { t: "Formations vidéo", d: "Des expériences d'apprentissage structurées à partir de vos connaissances. Nous gérons le curriculum, le support de production et l'infrastructure de vente." },
        { t: "Programmes de coaching", d: "Des offres premium qui donnent à vos followers les plus engagés un accès direct, avec des systèmes qui vendent et délivrent." },
        { t: "Templates & outils", d: "Des ressources actionnables que votre audience peut utiliser immédiatement. Haute valeur perçue, rapide à produire, facile à déployer." },
      ]
    },
    faq: {
      eyebrow: "FAQ",
      title: "Des questions ?",
      titleItalic: "Nous avons les réponses.",
      items: [
        { q: "En quoi c'est différent d'une agence marketing ?", a: "Les agences facturent des forfaits mensuels et livrent des prestations. Nous sommes un partenaire de revenus, sans frais initiaux. Nous concevons, construisons, lançons et optimisons votre produit de A à Z, et nous ne gagnons que quand vous gagnez." },
        { q: "J'ai besoin d'une grosse communauté ?", a: "La qualité de l'engagement compte plus que la taille brute de l'audience. Une communauté soudée d'acheteurs peut générer des revenus significatifs même avec une audience modeste. Nous évaluons l'adéquation lors de l'audit de découverte." },
        { q: "Quel type de produits digitaux vous créez ?", a: "Principalement des formations vidéo, des programmes de coaching premium et des templates ou outils à fort levier. Chacun est rétro-conçu à partir des problèmes spécifiques de votre audience et de sa capacité à payer." },
        { q: "Combien de temps je dois y consacrer ?", a: "Très peu. Nous avons besoin de quelques appels stratégiques et de votre présence sur les visuels clés et le contenu de lancement. Tout le reste (création, copy, tech, ops), c'est nous." },
        { q: "Comment vous gagnez de l'argent ?", a: "Pur partage des revenus. Pas de frais d'installation, pas de forfait mensuel. Nos intérêts sont 100% alignés sur votre chiffre d'affaires." },
        { q: "En combien de temps je peux voir des résultats ?", a: "Le délai prévu entre l'audit et le premier lancement est de 4 à 8 semaines, selon la taille de l'audience, l'engagement et l'adéquation de l'offre." },
        { q: "Et si je n'ai jamais rien vendu ?", a: "Encore mieux. Nous gérons la stratégie produit, le positionnement et la séquence de lancement. Vous, vous montrez à votre audience comme vous le faites déjà." },
        { q: "C'est juste du contenu IA collé sur un produit ?", a: "Non. Nous concevons un curriculum original, écrivons un copy sur mesure et orchestrons chaque lancement autour de votre voix et de votre audience. L'IA est un outil que nous utilisons, pas le produit que nous livrons." },
      ]
    },
    finalCta: {
      eyebrow: "Prêt à commencer ?",
      title: "Votre audience attend déjà",
      titleItalic: "d'acheter chez vous.",
      sub: "Pas de frais initiaux. Pas de forfaits mensuels. Nous ne gagnons que quand vous gagnez. Nous travaillons avec un nombre limité de créateurs. Postulez maintenant et transformons le potentiel de votre audience en revenus concrets.",
      mail: "Envie de s'associer avec nous ? Envoyez-nous un email à",
      mailSuffix: "avec votre nom, niche et pseudo Instagram.",
      micro: "Pas de spam. Pas d'engagement. Juste un échange sur votre business.",
      tagline: "0 € de risque. 0 € d'avance. Nous gagnons ensemble.",
      cta: "Postuler",
    },
    footer: {
      blurb: "Transformer l'attention des créateurs en revenus durables.",
      rights: "Tous droits réservés.",
    }
  }
};
